/**
 * Reads a JWT without verifying it, the one thing the dashboard ever did with `jsonwebtoken`.
 *
 * `jsonwebtoken` is a Node library: in the browser it pulled crypto-browserify, bn.js and
 * elliptic (~1 MB) into the entry bundle just to base64-decode two segments. The server
 * verifies every token; the client only needs the claims.
 */

export interface DecodedJwt {
  header: Record<string, unknown>;
  payload: Record<string, any>;
  signature: string;
}

function decodeSegment(segment: string): Record<string, any> {
  const base64 = segment
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(segment.length / 4) * 4, '=');
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

/**
 * Same contract as `jwt.decode(token, { complete: true })`: null when the token is not a JWT.
 * The options argument is accepted (and ignored) so call sites read like jsonwebtoken.
 */
const decode: (
  token: string | null | undefined,
  options?: { complete?: boolean }
) => DecodedJwt | null = token => {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = decodeSegment(parts[1]);
    if (!payload || typeof payload !== 'object') return null;
    return { header: decodeSegment(parts[0]), payload, signature: parts[2] };
  } catch {
    return null;
  }
};

const jwt = { decode };
export default jwt;
